<script>
  // @ts-check
  import { v4 as uuid } from "@lukeed/uuid";
  import { fade } from "svelte/transition";
  import { cubicInOut } from "svelte/easing";

  import Icon from "../../atoms/Icon.svelte";
  import LoadingIndicator from "../../atoms/LoadingIndicator.svelte";

  import SearchBar from "./SearchBar.svelte";
  import SearchQuerySuggestions from "./SearchQuerySuggestions.svelte";

  /** Primarily used for testing
   * @type {string} */
  export let id = undefined;

  /** Search ID
   * @type {string} */
  export let searchId = null;

  /** The `autocomplete` prop determines whether the browser should automatically complete the input value.
   * @type {string | undefined} */
  export let autocomplete = "off";

  /** The error message to display
   * @type {string} */
  export let errorMessage = null;

  /** Search results loading state
   * @type {boolean} */
  export let isLoading = false;

  /** Default elected value of the search input
   * @type {string} */
  export let selectedValue = null;

  /** Field should be focused on mount
   * @type {boolean} */
  export let focusOnMount = false;

  /**
   * The text to be read by screen readers when results are loading
   * @type {string}
   */
  export let loadingAccessibilityLabel = null;

  /** @typedef {import("svelte").ComponentProps<SearchQuerySuggestions>} SearchQuerySuggestionsProps */
  /** SearchQuerySuggestions props
   * @type {SearchQuerySuggestionsProps} */
  export let searchQuerySuggestionsProps = null;

  /** @type { string } */
  let componentClass = "";
  export { componentClass as class };

  let searchDefaultId = searchId || uuid();
</script>

<div {id} class={componentClass}>
  <SearchBar
    on:submit
    on:clear
    on:change
    on:input
    searchId={searchDefaultId}
    selectedValue={searchQuerySuggestionsProps?.selectedItem || selectedValue}
    {autocomplete}
    {focusOnMount}
  />
  <div class="flex w-full items-end md:h-[45px]">
    {#if isLoading}
      <div
        class="w-full"
        in:fade={{ duration: 150, delay: 150, easing: cubicInOut }}
        out:fade={{ duration: 150, easing: cubicInOut }}
      >
        <LoadingIndicator class="mt-5" accessibilityLabel={loadingAccessibilityLabel} />
      </div>
    {:else if errorMessage}
      <div aria-live="assertive" class="flex items-center gap-2 pt-5">
        <Icon iconClass="text-system-red h-4 w-4" name="alert" />
        <div id={`${id}-error`} class="label text-system-red" role="alert">{errorMessage}</div>
      </div>
    {:else if searchQuerySuggestionsProps}
      <div
        in:fade={{ duration: 150, delay: 150, easing: cubicInOut }}
        out:fade={{ duration: 150, easing: cubicInOut }}
      >
        <SearchQuerySuggestions
          class="pt-5"
          on:querySuggestionItemSelect
          {...searchQuerySuggestionsProps}
        />
      </div>
    {/if}
  </div>
</div>
